import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useBooking } from "@/lib/booking-context";

const links = [
  { label: "Experiences", id: "experiences" },
  { label: "Our Standard", id: "standard" },
  { label: "How It Works", id: "how-it-works" },
  { label: "Reviews", id: "reviews" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Navbar = () => {
  const { openBooking } = useBooking();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-warmwhite/85 backdrop-blur-xl border-b border-stone/60" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto max-w-[1400px] px-6 sm:px-8 md:px-12 h-20 flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className={`font-serif text-xl md:text-[1.35rem] tracking-tight leading-none transition-colors ${scrolled ? "text-charcoal" : "text-warmwhite"}`}
          data-testid="brand-logo"
        >
          The <span className="italic">Standard</span>
        </button>

        <div className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className={`link-underline text-sm font-medium transition-colors ${scrolled ? "text-charcoal/80 hover:text-charcoal" : "text-warmwhite/90 hover:text-warmwhite"}`}
              data-testid={`nav-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={openBooking}
            className={`px-6 py-2.5 text-sm font-semibold rounded-full transition-[transform,background-color,color,box-shadow] duration-300 hover:-translate-y-0.5 active:scale-[0.97] ${
              scrolled ? "bg-charcoal text-warmwhite hover:bg-champagne hover:shadow-[0_10px_30px_rgba(197,165,114,0.4)]" : "bg-warmwhite text-charcoal hover:bg-champagne hover:text-warmwhite hover:shadow-[0_10px_30px_rgba(0,0,0,0.25)]"
            }`}
            data-testid="nav-schedule-btn"
          >
            Schedule Your Cleaning
          </button>
        </div>

        <button
          className={`md:hidden flex flex-col items-end justify-center gap-2 p-2 -mr-2 min-w-11 min-h-11 ${scrolled ? "text-charcoal" : "text-warmwhite"}`}
          onClick={() => setMenuOpen(true)}
          data-testid="mobile-menu-open"
          aria-label="Open menu"
        >
          <span className="block w-7 h-px bg-current" />
          <span className="block w-7 h-px bg-current" />
          <span className="block w-5 h-px bg-current self-end" />
        </button>
      </nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-warmwhite md:hidden flex flex-col px-8 py-10"
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center mb-16">
              <span className="font-serif text-xl text-charcoal">The <span className="italic">Standard</span></span>
              <button onClick={() => setMenuOpen(false)} className="text-charcoal text-3xl leading-none" data-testid="mobile-menu-close" aria-label="Close menu">×</button>
            </div>
            <div className="flex flex-col gap-8">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                  onClick={() => { setMenuOpen(false); setTimeout(() => scrollTo(l.id), 300); }}
                  className="font-serif text-3xl text-charcoal text-left"
                  data-testid={`mobile-nav-${l.id}`}
                >
                  {l.label}
                </motion.button>
              ))}
            </div>
            <button
              onClick={() => { setMenuOpen(false); setTimeout(openBooking, 300); }}
              className="mt-auto bg-charcoal text-warmwhite py-4 rounded-full font-semibold"
              data-testid="mobile-schedule-btn"
            >
              Schedule Your Cleaning
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
